/**
 * SeedSystem — the hidden cooperative mini-game.
 *
 * After a memory completes, there's a chance a glowing seed appears
 * drifting across the garden. Either player can tap to "carry" it —
 * the seed follows their finger. When they release, it stays put.
 *
 * "Passing" means both players have carried the seed at least once,
 * alternating. After 3+ successful passes (A→B→A), the seed "roots"
 * wherever it lands and grows into a hidden 5th plant with its own
 * live-generated Grandma memory.
 *
 * The system lives inside GardenScene — this file is a helper class.
 */
import Phaser from 'phaser';
import { Transport } from './Transport.js';
import { SFX } from './SFX.js';

export class SeedSystem {
  constructor(scene) {
    this.scene = scene;
    this.active = false;
    this.seed = null;         // visual objects
    this.carrier = null;      // clientId currently carrying, or null if drifting
    this.lastCarrier = null;  // who carried it last (to detect passes)
    this.passCount = 0;       // successful passes (carrier changes)
    this.rootedAt = null;     // { x, y } when it finally roots
    this.driftTween = null;
    this._wireTransport();
  }

  _wireTransport() {
    Transport.on('seed_spawn', (payload) => this.remoteSpawn(payload));
    Transport.on('seed_grab', (payload, fromId) => this.remoteGrab(fromId));
    Transport.on('seed_move', (payload) => this.remoteMove(payload));
    Transport.on('seed_release', (payload) => this.remoteRelease(payload));
    Transport.on('seed_root', (payload) => this.remoteRoot(payload));
  }

  spawn() {
    if (this.active) return;
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;
    // Random start position along upper half
    const x = w * (0.2 + Math.random() * 0.6);
    const y = h * (0.3 + Math.random() * 0.2);
    this._createVisual(x, y);
    this._startDrift();
    this.active = true;
    this.passCount = 0;
    this.lastCarrier = null;
    this.carrier = null;
    // Tell the peer
    Transport.send('seed_spawn', { x: x / w, y: y / h });
  }

  remoteSpawn(payload) {
    if (this.active) return;
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;
    this._createVisual(payload.x * w, payload.y * h);
    this._startDrift();
    this.active = true;
    this.passCount = 0;
    this.lastCarrier = null;
    this.carrier = null;
  }

  _createVisual(x, y) {
    const s = this.scene;
    const obj = { x, y };
    // Outer glow
    obj.halo = s.add.circle(x, y, 18, 0xfff4a0, 0.25).setDepth(36);
    // Bright core
    obj.core = s.add.circle(x, y, 6, 0xfff4a0, 1).setDepth(38);
    // Spark ring
    obj.ring = s.add.circle(x, y, 10, 0xfff4a0, 0).setDepth(37).setStrokeStyle(1.5, 0xfff4a0, 0.8);
    // Label
    obj.label = s.add.text(x, y + 28, 'a seed — pass it between you', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '12px',
      fontStyle: 'italic',
      color: '#fff4a0',
      stroke: '#0a0612',
      strokeThickness: 3,
    }).setOrigin(0.5).setDepth(38).setAlpha(0.9);
    // Pulse
    s.tweens.add({
      targets: [obj.halo, obj.ring],
      scale: { from: 0.9, to: 1.4 }, alpha: { from: 0.25, to: 0.55 },
      duration: 1200, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
    // Hit zone — click/tap to grab
    obj.hit = s.add.circle(x, y, 30, 0x000000, 0).setDepth(39).setInteractive({ useHandCursor: true });
    obj.hit.on('pointerdown', (pointer, lx, ly, event) => {
      event.stopPropagation();
      this.grab();
    });
    this.seed = obj;
  }

  _startDrift() {
    if (!this.seed) return;
    const drift = () => {
      if (!this.seed || this.carrier) return;
      const s = this.scene;
      const tx = s.scale.width * (0.15 + Math.random() * 0.7);
      const ty = s.scale.height * (0.3 + Math.random() * 0.4);
      this.driftTween = s.tweens.add({
        targets: [this.seed.halo, this.seed.core, this.seed.ring, this.seed.label, this.seed.hit],
        x: { value: tx, ease: 'Sine.easeInOut' },
        duration: 4500 + Math.random() * 2000,
        onUpdate: () => {
          this.seed.x = this.seed.core.x;
          this.seed.y = this.seed.core.y;
          if (this.seed.label) this.seed.label.y = this.seed.core.y + 28;
        },
        onComplete: drift,
      });
      s.tweens.add({
        targets: [this.seed.halo, this.seed.core, this.seed.ring, this.seed.hit],
        y: { value: ty, ease: 'Sine.easeInOut' },
        duration: 4500 + Math.random() * 2000,
      });
    };
    drift();
  }

  grab() {
    if (!this.active || !this.seed) return;
    const myId = Transport.myId || 'me';
    // Can't grab if I was the last carrier (must pass to peer first)
    if (this.lastCarrier === myId) {
      // Visual hint: small shake
      this.scene.tweens.add({ targets: [this.seed.core], angle: { from: -8, to: 8 }, duration: 80, yoyo: true, repeat: 2 });
      return;
    }
    this.carrier = myId;
    if (this.driftTween) { this.driftTween.remove(); this.driftTween = null; }
    this.scene.tweens.killTweensOf([this.seed.halo, this.seed.core, this.seed.ring, this.seed.label, this.seed.hit]);
    SFX.tap();
    Transport.send('seed_grab', {});
    // Bind pointer movement to seed position
    this._pointerMoveHandler = (pointer) => {
      if (this.carrier !== myId || !this.seed) return;
      this.seed.halo.x = pointer.x; this.seed.halo.y = pointer.y;
      this.seed.core.x = pointer.x; this.seed.core.y = pointer.y;
      this.seed.ring.x = pointer.x; this.seed.ring.y = pointer.y;
      this.seed.hit.x = pointer.x;  this.seed.hit.y = pointer.y;
      this.seed.label.x = pointer.x; this.seed.label.y = pointer.y + 28;
      this.seed.x = pointer.x; this.seed.y = pointer.y;
      Transport.send('seed_move', { nx: pointer.x / this.scene.scale.width, ny: pointer.y / this.scene.scale.height });
    };
    this._pointerUpHandler = () => this.release();
    this.scene.input.on('pointermove', this._pointerMoveHandler);
    this.scene.input.once('pointerup', this._pointerUpHandler);
  }

  release() {
    if (!this.seed || !this.carrier) return;
    const myId = Transport.myId || 'me';
    if (this.carrier !== myId) return;
    this.scene.input.off('pointermove', this._pointerMoveHandler);
    if (this.lastCarrier && this.lastCarrier !== myId) {
      // This was a successful pass (from peer to me and I'm releasing)
      this.passCount += 1;
    } else if (!this.lastCarrier) {
      // First grab — no pass yet
    }
    this.lastCarrier = myId;
    this.carrier = null;
    Transport.send('seed_release', { nx: this.seed.x / this.scene.scale.width, ny: this.seed.y / this.scene.scale.height });
    // Check if we hit the pass goal
    if (this.passCount >= 3) {
      this.root(this.seed.x, this.seed.y);
      return;
    }
    // Resume drift — slowly
    this._startDrift();
  }

  remoteGrab(fromId) {
    if (!this.seed) return;
    // Peer grabbed the seed from their end
    this.carrier = fromId;
    if (this.driftTween) { this.driftTween.remove(); this.driftTween = null; }
    this.scene.tweens.killTweensOf([this.seed.halo, this.seed.core, this.seed.ring, this.seed.label, this.seed.hit]);
    SFX.tap();
  }

  remoteMove(payload) {
    if (!this.seed) return;
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;
    const x = payload.nx * w;
    const y = payload.ny * h;
    this.seed.halo.x = x; this.seed.halo.y = y;
    this.seed.core.x = x; this.seed.core.y = y;
    this.seed.ring.x = x; this.seed.ring.y = y;
    this.seed.hit.x = x;  this.seed.hit.y = y;
    this.seed.label.x = x; this.seed.label.y = y + 28;
    this.seed.x = x; this.seed.y = y;
  }

  remoteRelease(payload) {
    if (!this.seed) return;
    const fromId = this.carrier;
    // Peer released — check if they were the other side of a pass
    if (this.lastCarrier && this.lastCarrier !== fromId) {
      this.passCount += 1;
    }
    this.lastCarrier = fromId;
    this.carrier = null;
    if (this.passCount >= 3) {
      this.root(this.seed.x, this.seed.y);
      return;
    }
    this._startDrift();
  }

  root(x, y) {
    if (!this.seed) return;
    // Visual: big burst + seed sinks + sprout
    const s = this.scene;
    SFX.memoryUnlock();
    if (navigator.vibrate) navigator.vibrate([40, 30, 80]);
    // Burst particles
    for (let i = 0; i < 18; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 40 + Math.random() * 60;
      const p = s.add.circle(x, y, 2 + Math.random() * 2, 0xfff4a0, 1).setDepth(40);
      s.tweens.add({
        targets: p,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist,
        alpha: 0, scale: 0.3,
        duration: 1000 + Math.random() * 500,
        ease: 'Quad.easeOut',
        onComplete: () => p.destroy(),
      });
    }
    // Clean up seed visual
    this.rootedAt = { x, y };
    const toKill = [this.seed.halo, this.seed.core, this.seed.ring, this.seed.label, this.seed.hit];
    s.tweens.add({
      targets: toKill, alpha: 0, duration: 500,
      onComplete: () => { toKill.forEach(o => o?.destroy()); this.seed = null; },
    });
    this.active = false;
    Transport.send('seed_root', { nx: x / s.scale.width, ny: y / s.scale.height });
    // Tell the scene to spawn the secret 5th plant
    if (this.scene._onSeedRooted) this.scene._onSeedRooted(x, y);
  }

  remoteRoot(payload) {
    if (!this.seed) return;
    const x = payload.nx * this.scene.scale.width;
    const y = payload.ny * this.scene.scale.height;
    this.root(x, y);
  }

  cleanup() {
    if (this.seed) {
      [this.seed.halo, this.seed.core, this.seed.ring, this.seed.label, this.seed.hit].forEach(o => o?.destroy());
      this.seed = null;
    }
    this.active = false;
  }
}
